import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import WarningIcon from "@mui/icons-material/Warning";
import AddIcon from "@mui/icons-material/Add";
import { Modal as BaseModal } from "@mui/base/Modal";
import React from "react";
import { useState, useEffect, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { TEquimetre } from "../../shared/types/training.type";
import {
  setEqmSubscriptionItem,
  setEqmSubscriptionModal,
} from "../../store/reducers/modal.reducer";
import { t } from "i18next";
import { useMyEquimetres } from "../../hooks/useMyEquimetres";
import { useHubstripe } from "../../hooks/useHubstripe";
import { LoadingButton } from "@mui/lab";
import { TBilling } from "../../shared/types/billing.type";
import { get, isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import clsx from "clsx";
import { styled, css } from "@mui/system";

import BillingPaymentMethodsItemsTable from "../../components/user/billings/BillingPaymentMethodsItemsTable";
import BillingPaymentMethodsForm from "../../components/user/billings/BillingPaymentMethodsForm";

const stripePromise = loadStripe("pk_test_Yx6K96JLMfn9lz8meeV756FW00rpNQiGIZ"); //loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? '');

function EqmSubscribeModal() {
  const open = useSelector<RootState>(
    (state) => state.modals.eqmSubModal,
  ) as boolean;

  const equimetre = useSelector<RootState>(
    (state) => state.modals.eqmToSubscribe,
  ) as TEquimetre | null;

  //retrieve user billings
  const billings = useSelector<RootState>(
    (state) => state.user.user?.user_billing_infos,
  ) as TBilling[];

  const dispatch = useDispatch();

  const { subscribeMutation } = useMyEquimetres({ enableQuery: false });
  const { getPaymentMethodsFromCustomerId, getCustomerFromCustomerId } =
    useHubstripe();

  const queryClient = useQueryClient();

  const handleClose = () => {
    dispatch(setEqmSubscriptionItem(null));
    dispatch(setEqmSubscriptionModal(false));
  };

  const [paymentMethods, setPaymentMethods] = useState<any>([]);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [customer, setCustomer] = useState<any>(null);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    string | null
  >(null);

  const [openModalPaymentMethods, setOpenModalPaymentMethods] = useState(false);
  const handleOpenModalPaymentMethods = () => setOpenModalPaymentMethods(true);
  const handleCloseModalPaymentMethods = () =>
    setOpenModalPaymentMethods(false);

  const options: StripeElementsOptions = {
    mode: "setup",
    currency: customer?.currency ?? "usd",
    // Fully customizable with appearance API.
    appearance: {
      theme: "stripe",
    },
  };

  const priceWithCurrency = (customer: any) => {
    if (customer?.currency != null) {
      switch (customer.currency) {
        case "eur":
          return "35 €";
        case "usd":
          return "35 $";
        case "gbp":
          return "28 £";
        case "aud":
          return "56 $A";
        case "nzd":
          return "70 NZD";
        default:
          return "35 €";
      }
    } else {
      return "35 $";
    }
  };

  const fetchData = async (customerId: string) => {
    try {
      const jsonPaymentMethods =
        await getPaymentMethodsFromCustomerId.mutateAsync(customerId);
      const jsonCustomer =
        await getCustomerFromCustomerId.mutateAsync(customerId);
      setPaymentMethods(jsonPaymentMethods.data.paymentMethods.data);
      setCustomer(jsonCustomer.data.customer);
    } catch (err) {
      console.log({ err });
    }
  };

  const subscribe = async () => {
    if (!equimetre || isEmpty(billings)) return;
    try {
      await subscribeMutation.mutateAsync({
        data: {
          id: equimetre.mac_address.id,
          customer: get(billings, "[0].stripe_id"),
          payment_method_id: selectedPaymentMethodId ?? "",
        },
      });
      queryClient.refetchQueries(["eqms"]);
      toast(t("rights:success"), { type: "success" });
      setSuccessDialogOpen(true); // ← Affiche le Dialog
    } catch (error) {
      console.log(error);
      setSuccessDialogOpen(true); // ← Affiche le Dialog
      toast(t("error:generic"), { type: "error" });
    }
  };

  const fetchByStripeId = () => {
    let stripe_id = get(billings, "[0].stripe_id");
    if (!stripe_id) return;

    fetchData(stripe_id);
  };

  const onPaymentMethodsChange = () => {
    fetchByStripeId();
  };

  useEffect(() => {
    fetchByStripeId();
  }, [billings]);

  if (!open) return null;

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ m: 0, p: 2 }}>
          {t("liveSync:dialog_3:title")} :<br />
          {equimetre?.display_name ?? equimetre?.mac_address.mac_address}
        </DialogTitle>
        <DialogContent>
          <Alert
            icon={<CheckIcon fontSize="inherit" />}
            severity="success"
            sx={{ mb: 2 }}
          >
            {t("liveSync:dialog_3:content:compatibility")}
          </Alert>
          <Alert
            icon={<AnnouncementIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: 2 }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t("liveSync:dialog_3:content:price", {
                  price: priceWithCurrency(customer),
                  interpolation: {
                    escapeValue: false,
                  },
                }),
              }}
            />
          </Alert>

          <Card sx={{ p: 3 }}>
            {paymentMethods != null && customer != null && (
              <div>
                <Stack spacing={2}>
                  <Typography
                    variant="h6"
                    color="inherit"
                    noWrap
                    sx={{ flexGrow: 1 }}
                  >
                    {t("liveSync:dialog_3:content:payment:title")}
                    <Modal
                      aria-labelledby="unstyled-modal-title"
                      aria-describedby="unstyled-modal-description"
                      open={openModalPaymentMethods}
                      onClose={handleCloseModalPaymentMethods}
                      slots={{ backdrop: StyledBackdrop }}
                    >
                      <ModalContent sx={{ width: 800 }}>
                        {/* Stripe */}
                        <Elements stripe={stripePromise} options={options}>
                          <BillingPaymentMethodsForm
                            customer={customer}
                            onSuccess={() => {
                              handleCloseModalPaymentMethods(); // ferme juste la modale
                              fetchByStripeId(); // recharge les méthodes de paiement
                            }}
                            onFailure={() => {
                              toast(t("error:generic"), { type: "error" });
                            }}
                          />
                        </Elements>
                      </ModalContent>
                    </Modal>
                  </Typography>

                  <BillingPaymentMethodsItemsTable
                    customer={customer}
                    paymentMethods={paymentMethods}
                    onPaymentMethodsChange={onPaymentMethodsChange}
                    loadingPaymentMethods={loadingPaymentMethods}
                    selectedPaymentMethodId={selectedPaymentMethodId}
                    onSelectPaymentMethod={setSelectedPaymentMethodId}
                  />
                </Stack>
              </div>
            )}
          </Card>
        </DialogContent>
        <DialogActions>
          <Box display="flex" justifyContent="space-between" width="100%">
            <Button onClick={handleOpenModalPaymentMethods}>
              {t("liveSync:dialog_3:content:payment:add")}
            </Button>
            <Box>
              <Button onClick={handleClose}>{t("button:cancel")}</Button>
              <LoadingButton
                loading={subscribeMutation.isLoading}
                variant="contained"
                onClick={subscribe}
                autoFocus
                sx={{ ml: 2 }}
                disabled={!selectedPaymentMethodId}
              >
                {t("eqmSub:pay")}
              </LoadingButton>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ m: 0, p: 2 }}>
          {t("liveSync:dialog_4:title")}
        </DialogTitle>
        <DialogContent>
          <Alert
            icon={<AnnouncementIcon fontSize="inherit" />}
            severity="info"
            sx={{ mb: 2 }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: t("liveSync:dialog_4:content"),
              }}
            />
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleClose();
              setSuccessDialogOpen(false);
            }}
            autoFocus
          >
            {t("liveSync:dialog_4:validation")}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

// Define the types for Backdrop props
interface BackdropProps {
  open?: boolean;
  className?: string;
}

const Backdrop = forwardRef<HTMLDivElement, BackdropProps>(
  ({ open, className, ...other }, ref) => {
    return (
      <div
        className={clsx({ "base-Backdrop-open": open }, className)}
        ref={ref}
        {...other}
      />
    );
  },
);

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Modal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled("div")(
  ({ theme }) => css`
    font-family: "IBM Plex Sans", sans-serif;
    font-weight: 500;
    text-align: center;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    background-color: ${"#fff"};
    border-radius: 8px;
    border: 1px solid ${grey[200]};
    box-shadow: 0 4px 12px ${"rgb(0 0 0 / 0.2)"};
    padding: 24px;
    color: ${grey[900]};

    & .modal-title {
      margin: 0;
      line-height: 1.5rem;
      margin-bottom: 8px;
    }

    & .modal-description {
      margin: 0;
      line-height: 1.5rem;
      font-weight: 400;
      color: ${grey[800]};
      margin-bottom: 4px;
    }
  `,
);

export default EqmSubscribeModal;
